<template>
  <Dialog
    :visible="visible"
    modal
    :header="t('customStats.delete.title')"
    :style="{ width: '500px' }"
    :draggable="false"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="space-y-4">
      <Message severity="error" :closable="false">
        {{ t('customStats.delete.warningIrreversible') }}
      </Message>

      <p>
        {{ t('customStats.delete.subtitle') }}
      </p>

      <div class="bg-gray-50 p-3 rounded">
        <strong>{{ t('customStats.delete.kpiName') }}</strong>
        <div class="font-mono text-base">{{ displayedName }}</div>
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">
          {{ t('customStats.delete.confirmLabel', { word: confirmWord }) }}
        </label>
        <InputText
          v-model="confirmation"
          class="w-full"
          :placeholder="confirmWord"
          :class="{ 'p-invalid': confirmation && !isMatch }"
        />
      </div>

      <div v-if="errorMessage" class="text-red-600 text-sm">
        {{ errorMessage }}
      </div>
    </div>

    <template #footer>
      <Button
        :label="t('common.cancel')"
        text
        @click="onCancel"
      />
      <Button
        :label="t('customStats.delete.confirmButton')"
        severity="danger"
        :disabled="!isMatch || isDeleting"
        :loading="isDeleting"
        @click="onConfirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { deleteKpi } from '@/composables/customStats/customKpi';
import type { CustomKpiDefinition } from '@/composables/customStats/customKpi.interfaces';

export default defineComponent({
  name: 'CustomKpiDeleteDialog',
  components: {
    Dialog,
    Button,
    InputText,
    Message,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    kpi: {
      type: Object as PropType<CustomKpiDefinition>,
      required: true,
    },
  },
  emits: ['update:visible', 'deleted'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const confirmation = ref('');
    const errorMessage = ref('');
    const isDeleting = ref(false);

    const displayedName = computed(() => {
      const n = props.kpi.name;
      return n?.[locale.value] ?? n?.en ?? Object.values(n ?? {})[0] ?? '';
    });

    // Le mot à taper est fixe selon la locale (FR='supprimer', EN='delete').
    // Le back accepte les deux quelque soit la langue (case-insensitive).
    const confirmWord = computed(() =>
      locale.value?.startsWith('fr') ? 'supprimer' : 'delete',
    );

    const isMatch = computed(
      () =>
        confirmation.value.trim().toLowerCase() ===
        confirmWord.value.toLowerCase(),
    );

    watch(
      () => props.visible,
      (v) => {
        if (v) {
          confirmation.value = '';
          errorMessage.value = '';
        }
      },
    );

    function onCancel() {
      emit('update:visible', false);
    }

    async function onConfirm() {
      if (!isMatch.value) return;
      isDeleting.value = true;
      errorMessage.value = '';
      try {
        await deleteKpi(props.kpi.id_custom_kpi, confirmation.value.trim());
        emit('deleted');
      } catch (err: any) {
        // err peut être un objet axios { message, status, ... } ou string.
        const msg = err?.message ?? err?.error ?? err;
        errorMessage.value =
          typeof msg === 'string' ? msg : JSON.stringify(msg);
      } finally {
        isDeleting.value = false;
      }
    }

    return {
      t,
      confirmation,
      errorMessage,
      isDeleting,
      displayedName,
      confirmWord,
      isMatch,
      onCancel,
      onConfirm,
    };
  },
});
</script>
